import { useId, type ReactNode } from 'react';
import { RadioGroup as Group } from '@base-ui/react/radio-group';
import { Radio } from '@base-ui/react/radio';
import { cn } from './cn';
import { focusRing } from './focus';

export type RadioOption<T extends string> = { value: T; label: string; icon?: ReactNode; /** `cards`: shown above the label. */ preview?: ReactNode };

/**
 * One choice among a few (Base UI radio group, arrow keys move and select). `list`: a column of labelled dots;
 * `segmented`: a row of joined buttons (background patterns, export formats); `cards`: a grid of bordered cards, each
 * with its preview above its dot and label (board templates), a press anywhere on the card selects it.
 */
export function RadioGroup<T extends string>({
  label,
  value,
  onValueChange,
  options,
  variant = 'list',
  disabled,
  className,
}: {
  label: string;
  value: T;
  onValueChange: (value: T) => void;
  options: readonly RadioOption<T>[];
  variant?: 'list' | 'segmented' | 'cards';
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const segmented = variant === 'segmented';
  const cards = variant === 'cards';
  return (
    <Group
      aria-label={label}
      value={value}
      disabled={disabled}
      onValueChange={(v) => onValueChange(v as T)}
      className={cn(segmented ? 'inline-flex rounded-md border border-border bg-surface-2 p-0.5' : cards ? 'grid grid-cols-2 gap-2' : 'flex flex-col gap-1', 'font-sans text-sm text-fg', className)}
    >
      {options.map((o) => (
        // A <span>, not a <label>: a label would also click Base UI's hidden input (see Checkbox). The whole row (44 px
        // on touch) or card selects on a press (P12a).
        <span
          key={o.value}
          data-hit-area=""
          onClick={
            segmented
              ? undefined
              : (e) => {
                  // The radio and its hidden input select by themselves: once, not twice.
                  if (disabled || (e.target instanceof Element && e.target.closest('[role="radio"], input'))) return;
                  onValueChange(o.value);
                }
          }
          className={cn(
            'inline-flex items-center gap-2 pointer-coarse:min-h-11',
            segmented && 'flex-1',
            cards && 'cursor-pointer flex-wrap rounded-md border border-border p-2 has-[[data-checked]]:border-accent',
          )}
        >
          {cards && o.preview && <span className="w-full">{o.preview}</span>}
          <Radio.Root
            value={o.value}
            aria-labelledby={`${id}-${o.value}`}
            className={cn(
              segmented
                ? 'inline-flex h-7 w-full items-center justify-center gap-1.5 rounded px-2.5 text-xs text-fg-muted transition-colors duration-(--duration-fast) data-[checked]:bg-surface data-[checked]:text-fg data-[checked]:shadow-sm pointer-coarse:h-11'
                : 'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border-strong bg-surface data-[checked]:border-accent',
              'data-[disabled]:opacity-50',
              focusRing,
            )}
          >
            {segmented ? (
              <>
                {o.icon}
                <span id={`${id}-${o.value}`}>{o.label}</span>
              </>
            ) : (
              <Radio.Indicator className="block h-2 w-2 rounded-full bg-accent" />
            )}
          </Radio.Root>
          {!segmented && (
            <span id={`${id}-${o.value}`}>
              {o.label}
            </span>
          )}
        </span>
      ))}
    </Group>
  );
}
